(function () {
  'use strict';

  const TRAITS = {
    A: { key: 'overthinker',    label: 'The Overthinker' },
    B: { key: 'analyst',        label: 'The Analyst' },
    C: { key: 'avoider',        label: 'The Avoider' },
    D: { key: 'people_pleaser', label: 'The People Pleaser' },
    E: { key: 'chaotic_wildcard', label: 'The Chaotic Wildcard' }
  };

  const QUESTIONS_PER_QUIZ = 8;

  // Conditional tiebreaker. Shown as the final question ONLY when the first
  // seven answers leave two or more traits level at the top. It offers just
  // those tied traits' identity lines, so the pick is a clean decider. Most
  // takers never see it (they have a clear leader by question eight).
  const TIEBREAKER = {
    prompt: 'Last one. Quick gut check, which is most you?',
    lines: {
      A: 'My brain refuses to let anything go',
      B: 'I need the full picture before I can move',
      C: "I'll deal with it later. Probably.",
      D: "I'll sort everyone else out first",
      E: "I'll just wing it and see what happens"
    }
  };

  // One-line "taste" of the result on the email gate — a hook keyed by primary
  // trait, NOT the full result. The full written result (secondary trait +
  // description) stays email-only.
  const TRAIT_TEASERS = {
    A: "Your brain replays it, rehearses it, and refuses to let it go. At 2am especially.",
    B: "You don't decide, you build a case. The spreadsheet is optional but let's be honest.",
    C: "You'll deal with it later. Or maybe you won't, and honestly, that's fine.",
    D: "Everyone else handled first. You're near the bottom of your own list. Again.",
    E: "No plan, total confidence, somehow it lands. Usually."
  };

  // Klaviyo Public API key (safe to expose in browser code — read-only / write-create only)
  const KLAVIYO_PUBLIC_KEY = 'S7kp3D';
  const KLAVIYO_LIST_ID    = 'T9NrzY';   // "Faffle Quiz Subscribers"
  const KLAVIYO_REVISION   = '2024-10-15';

  // Illustration assets — hosted on Shopify Files CDN so the same code runs
  // identically on the local prototype and on faffleandco.com.
  const ASSET_BASE = 'https://cdn.shopify.com/s/files/1/0780/9751/4709/files/';
  const ASSETS = {
    intro:            ASSET_BASE + 'quiz-intro.webp',
    overthinker:      ASSET_BASE + 'result-overthinker.webp',
    analyst:          ASSET_BASE + 'result-analyst.webp',
    avoider:          ASSET_BASE + 'result-avoider.webp',
    people_pleaser:   ASSET_BASE + 'result-people-pleaser.webp',
    chaotic_wildcard: ASSET_BASE + 'result-chaotic-wildcard.webp'
  };

  // ── Quiz state ───────────────────────────────────────────────
  const state = {
    questions: [],
    answers: [],
    currentIndex: 0,
    primaryLetter: null,
    secondaryLetter: null,
    isDoubleDown: false,
    recommendedProduct: null,
    firstName: '',
    email: ''
  };

  // ── Question selection ───────────────────────────────────────
  function pickQuestions(pool) {
    const byGroup = {};
    pool.forEach((q) => {
      if (!byGroup[q.group]) byGroup[q.group] = [];
      byGroup[q.group].push(q);
    });

    const picked = [];
    Object.keys(byGroup).forEach((g) => {
      const arr = byGroup[g];
      picked.push(arr[Math.floor(Math.random() * arr.length)]);
    });

    const remaining = pool.filter((q) => !picked.includes(q));
    while (remaining.length && picked.length < QUESTIONS_PER_QUIZ) {
      const idx = Math.floor(Math.random() * remaining.length);
      picked.push(remaining.splice(idx, 1)[0]);
    }

    return shuffle(picked).slice(0, QUESTIONS_PER_QUIZ);
  }

  function shuffle(arr) {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  // ── Scoring ──────────────────────────────────────────────────
  function score(answers) {
    const tally = { A: 0, B: 0, C: 0, D: 0, E: 0 };
    const firstSeen = {};
    answers.forEach((letter, idx) => {
      tally[letter]++;
      if (firstSeen[letter] === undefined) firstSeen[letter] = idx;
    });

    const counts = Object.entries(tally).filter(([, n]) => n > 0);
    counts.sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return firstSeen[a[0]] - firstSeen[b[0]];
    });

    const primaryLetter = counts[0][0];
    const isDoubleDown = counts[0][1] === answers.length;
    const secondaryLetter = isDoubleDown ? null : counts[1][0];

    return { primaryLetter, secondaryLetter, isDoubleDown };
  }

  // Which traits are level for the lead in a set of answers? Returns the tied
  // letters when two or more share the top count, or an empty array when there's
  // a clear leader. Used after question 7 to decide whether question 8 becomes
  // the tiebreaker.
  function leadingTie(answers) {
    const tally = {};
    answers.forEach((l) => { tally[l] = (tally[l] || 0) + 1; });
    const letters = Object.keys(tally);
    if (!letters.length) return [];
    const max = Math.max.apply(null, letters.map((l) => tally[l]));
    const leaders = letters.filter((l) => tally[l] === max);
    return leaders.length >= 2 ? leaders : [];
  }

  // Pick one random tee from the primary trait's live pool. The pool is rendered
  // into the page by Liquid (window.FAFFLE_TRAIT_PRODUCTS) so prices follow the
  // viewer's market and newly-tagged tees appear automatically. Picked once and
  // reused on both the email gate and the thank-you card. Returns null if the
  // pool is missing or empty — callers then fall back to the trait collection.
  function pickRecommendedProduct(letter) {
    const key = TRAITS[letter] && TRAITS[letter].key;
    const pool = (window.FAFFLE_TRAIT_PRODUCTS || {})[key] || [];
    if (!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  // ── Rendering ────────────────────────────────────────────────
  const $ = (sel) => document.querySelector(sel);

  function renderIntro() {
    const heartSVG = `<svg viewBox="0 0 24 21" width="14" height="12" fill="#D9A992" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 19c0 0-9-5.5-9-12c0-3 2-5 4.5-5C9 2 11 3 12 5c1-2 3-3 4.5-3C19 2 21 4 21 7c0 6.5-9 12-9 12z"/></svg>`;

    $('#screen').innerHTML = `
      <div class="screen intro">
        <div class="content">
          <p class="brand-mark">Faffle &amp; Co</p>
          <h1><span class="h1-small">What kind of</span><br><em>faffer</em> <span class="h1-small">are you?</span></h1>
          <div class="heart-divider">
            <span class="heart-line"></span>
            <span class="heart">${heartSVG}</span>
            <span class="heart-line"></span>
          </div>
          <div class="intro-cta">
            <p class="subhead">Eight questions. About a minute. <span class="confronting">Mildly confronting.</span></p>
            <button class="btn-primary" id="start-btn">Start the quiz</button>
          </div>
        </div>
        <img class="quiz-hero" src="${ASSETS.intro}" alt="">
      </div>`;
    $('#start-btn').addEventListener('click', startQuiz);
  }

  function startQuiz() {
    state.questions = pickQuestions(window.FAFFLE_QUIZ_QUESTIONS);
    state.answers = [];
    state.currentIndex = 0;
    renderQuestion();
  }

  function renderQuestion() {
    const total = state.questions.length;
    const num = state.currentIndex + 1;
    const pct = (num / total) * 100;
    const heartSVG = `<svg viewBox="0 0 24 21" width="20" height="18" fill="#D9A992" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 19c0 0-9-5.5-9-12c0-3 2-5 4.5-5C9 2 11 3 12 5c1-2 3-3 4.5-3C19 2 21 4 21 7c0 6.5-9 12-9 12z"/></svg>`;

    // The final question turns into the tiebreaker when the first seven answers
    // leave two or more traits level — it shows only those traits' identity
    // lines. Otherwise it's a normal question from the bank.
    const isLast = state.currentIndex === total - 1;
    const tied = isLast ? leadingTie(state.answers) : [];
    let prompt, optionList;
    if (tied.length >= 2) {
      prompt = TIEBREAKER.prompt;
      optionList = shuffle(tied.map((l) => ({ letter: l, text: TIEBREAKER.lines[l] })));
    } else {
      const q = state.questions[state.currentIndex];
      prompt = q.prompt;
      optionList = shuffle(q.options);
    }

    $('#screen').innerHTML = `
      <div class="screen question">
        <p class="progress-text">Question ${num} of ${total}</p>
        <div class="progress">
          <div class="progress-bar" style="width:${pct}%"></div>
          <span class="progress-marker" style="left:${pct}%">${heartSVG}</span>
        </div>
        <div class="prompt-card">
          <h2 class="prompt">${prompt}</h2>
        </div>
        <ul class="options">
          ${optionList
            .map(
              (o, i) => `
            <li>
              <button class="option" data-letter="${o.letter}" data-idx="${i}">
                <span class="option-num">${i + 1}</span>
                <span class="option-text">${o.text}</span>
              </button>
            </li>`
            )
            .join('')}
        </ul>
      </div>`;

    document.querySelectorAll('.option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const letter = btn.getAttribute('data-letter');
        btn.classList.add('selected');
        setTimeout(() => answerQuestion(letter), 220);
      });
    });
  }

  function answerQuestion(letter) {
    state.answers.push(letter);
    if (state.currentIndex < state.questions.length - 1) {
      state.currentIndex++;
      renderQuestion();
    } else {
      const result = score(state.answers);
      state.primaryLetter = result.primaryLetter;
      state.secondaryLetter = result.secondaryLetter;
      state.isDoubleDown = result.isDoubleDown;
      state.recommendedProduct = pickRecommendedProduct(state.primaryLetter);
      renderEmailGate();
    }
  }

  function renderEmailGate() {
    const trait = TRAITS[state.primaryLetter].label;
    const traitKey = TRAITS[state.primaryLetter].key;
    // Each word on its own line, full stop on the last.
    const traitWords = trait.split(' ');
    const traitStacked = traitWords
      .map((w, i) => (i === traitWords.length - 1 ? w + '.' : w))
      .join('<br>');
    const heartSVG = `<svg viewBox="0 0 24 21" width="14" height="12" fill="#D9A992" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 19c0 0-9-5.5-9-12c0-3 2-5 4.5-5C9 2 11 3 12 5c1-2 3-3 4.5-3C19 2 21 4 21 7c0 6.5-9 12-9 12z"/></svg>`;

    const teaser = TRAIT_TEASERS[state.primaryLetter] || '';
    const skipHref = state.recommendedProduct ? state.recommendedProduct.url : shopDestination();
    const skipLabel = state.recommendedProduct
      ? "Skip the email, see the tee we'd pick for you"
      : "Skip the email, browse your tees";

    $('#screen').innerHTML = `
      <div class="screen email-gate">
        <div class="content">
          <p class="brand-mark">Your Faffle type today is…</p>
          <h1><em>${traitStacked}</em></h1>
          <p class="trait-teaser">${teaser}</p>
          <div class="heart-divider">
            <span class="heart-line"></span>
            <span class="heart">${heartSVG}</span>
            <span class="heart-line"></span>
          </div>
          <p class="subhead">
            <span class="subhead-line subhead-line--terracotta">Want the full Faffle Report?</span>
            <span class="subhead-line subhead-line--sage">Pop your email below. We have a few more thoughts.</span>
          </p>
          <form id="email-form" novalidate>
            <input
              type="text"
              id="name-input"
              placeholder="First name (optional)"
              autocomplete="given-name"
            />
            <input
              type="email"
              id="email-input"
              placeholder="you@example.com"
              autocomplete="email"
              required
            />
            <!-- Honeypot: hidden from real users, bots auto-fill anything. If it has a value on submit, silently drop. -->
            <input
              type="text"
              id="hp-website"
              name="website"
              tabindex="-1"
              autocomplete="off"
              aria-hidden="true"
              style="position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;"
            />
            <button type="submit" class="btn-primary">Send me my Faffle Report</button>
            <p class="email-disclaimer">We'll email your result plus the occasional Faffle thing. Unsubscribe anytime.</p>
            <p class="error" id="email-error" hidden></p>
          </form>
          <div class="email-gate-footer">
            <a class="quiz-shop-link" href="${skipHref}">${skipLabel} &rarr;</a>
          </div>
        </div>
        <img class="quiz-hero" src="${ASSETS[traitKey]}" alt="">
      </div>`;

    $('#email-form').addEventListener('submit', (ev) => {
      ev.preventDefault();

      // Honeypot — if a bot filled the hidden field, fake success and bail
      if ($('#hp-website').value) {
        renderTeaser();
        return;
      }

      const email = $('#email-input').value.trim();
      const firstName = $('#name-input').value.trim();
      if (!isValidEmail(email)) {
        showEmailError("That doesn't look like an email — try again?");
        return;
      }
      state.email = email;
      state.firstName = firstName;
      submitToKlaviyo()
        .then(() => {
          // The Meta "Lead" event is fired on the page they LAND on (see
          // goToResult), NOT here. Firing it right before the redirect got the
          // beacon cancelled mid-flight, so Facebook only recorded ~1 in 7.
          goToResult();
        })
        .catch((err) => {
          console.error(err);
          showEmailError('Something went wrong. Try again in a sec?');
        });
    });
  }

  function isValidEmail(s) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  // After the email is captured, send them to their recommended tee's product
  // page with the result popup overlaid (?fr=1 → zoe-faffle-result.js renders it).
  // Closing the popup reveals the product underneath. Falls back to the thank-you
  // screen only if there's somehow no product to land on.
  function goToResult() {
    const product = state.recommendedProduct;
    if (!product || !product.url) {
      // No product to redirect to, so we stay on the teaser. Fire the Meta Lead
      // event here, where there's no navigation to cancel the beacon.
      if (typeof fbq === 'function') { try { fbq('track', 'Lead'); } catch (e) {} }
      renderTeaser();
      return;
    }
    const trait = TRAITS[state.primaryLetter].key;
    const secondary = state.secondaryLetter ? TRAITS[state.secondaryLetter].key : 'none';
    const sep = product.url.indexOf('?') === -1 ? '?' : '&';
    // lead=1 tells the result popup (zoe-faffle-result.js) to fire the Meta Lead
    // event after they land on the product page, where it can't be cancelled.
    window.location.href = product.url + sep
      + 'fr=1'
      + '&lead=1'
      + '&trait=' + encodeURIComponent(trait)
      + '&secondary=' + encodeURIComponent(secondary)
      + '&name=' + encodeURIComponent(state.firstName || '');
  }

  function showEmailError(msg) {
    const e = $('#email-error');
    e.textContent = msg;
    e.hidden = false;
  }

  // Where "shop your tees" buttons point. With a secondary trait we get sneaky:
  // storefront search ORs both trait tags so the customer sees their full
  // personality on one page (verified live: "quiz-analyst OR quiz-avoider" -> 7 tees).
  // Double-downs go to their trait collection (nicer banner, same products).
  function shopDestination() {
    const primary = TRAITS[state.primaryLetter].key.replace(/_/g, '-');
    if (state.secondaryLetter) {
      const secondary = TRAITS[state.secondaryLetter].key.replace(/_/g, '-');
      return '/search?q=' + encodeURIComponent('quiz-' + primary + ' OR quiz-' + secondary) + '&type=product';
    }
    return '/collections/the-' + primary;
  }

  function shopLabel() {
    return 'Shop your ' + TRAITS[state.primaryLetter].label.replace(/^The /, '') + ' tees';
  }

  // Provider-aware "find your email" tip for the thanks screen. Gmail files
  // designed emails under Promotions; Outlook prefers Junk. Telling people
  // exactly where to look (and to rescue us) trains their inbox for next time.
  function inboxTip() {
    var domain = (state.email || '').split('@')[1] || '';
    if (/gmail|googlemail/.test(domain)) {
      return 'Gmail sometimes files us under <strong>Promotions</strong>... if we\'re hiding there, drag the email to <strong>Primary</strong> so Gmail knows we\'re friends.';
    }
    if (/hotmail|outlook|live\.|msn/.test(domain)) {
      return 'Outlook can be suspicious of new friends... if we\'re not in your inbox, check <strong>Junk</strong> and mark us "Not junk" so we behave next time.';
    }
    if (/yahoo|ymail/.test(domain)) {
      return 'Yahoo sometimes tucks us into <strong>Spam</strong>... if so, hit <strong>"Not spam"</strong> and we\'ll arrive properly from now on.';
    }
    return 'If it doesn\'t come through soon, check your <strong>Spam</strong> or <strong>Promotions</strong> folder... and rescue us if we\'re in there.';
  }

  function renderTeaser() {
    const heartSVG = `<svg viewBox="0 0 24 21" width="14" height="12" fill="#D9A992" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 19c0 0-9-5.5-9-12c0-3 2-5 4.5-5C9 2 11 3 12 5c1-2 3-3 4.5-3C19 2 21 4 21 7c0 6.5-9 12-9 12z"/></svg>`;

    // The recommended tee (live data from Liquid) is the primary CTA. If the
    // live pool is missing, fall back to the old "View {trait} Tees" button.
    const rec = state.recommendedProduct;
    const traitShort = TRAITS[state.primaryLetter].label.replace(/^The /, '');
    const recCardHtml = rec ? `
            <div class="product-rec">
              <p class="product-rec-eyebrow">The tee we'd pick for you</p>
              <div class="product-rec-card">
                <img class="product-rec-img" src="${rec.image}" alt="${escapeHtml(rec.title)}" loading="lazy">
                <div class="product-rec-meta">
                  <p class="product-rec-name">${escapeHtml(rec.title)}</p>
                  <p class="product-rec-price">${escapeHtml(rec.price)}</p>
                  <a class="btn-primary product-rec-btn" href="${rec.url}">View Product &rarr;</a>
                  <a class="quiz-shop-link product-rec-browse" href="${shopDestination()}">or see all ${traitShort} tees &rarr;</a>
                </div>
              </div>
            </div>` : `
            <a class="btn-primary" href="${shopDestination()}">View ${traitShort} Tees &rarr;</a>`;

    $('#screen').innerHTML = `
      <div class="screen teaser thanks">
        <div class="content">
          <p class="brand-mark">Thanks for…</p>
          <h1><span class="h1-small h1-small--sage">Having fun with the</span><br><em>Faffle Fam.</em></h1>
          <div class="heart-divider">
            <span class="heart-line"></span>
            <span class="heart">${heartSVG}</span>
            <span class="heart-line"></span>
          </div>
          <div class="intro-cta">
            <p class="subhead">We sent your fun personality results to <strong>${escapeHtml(state.email)}</strong>.</p>
            <p class="teaser-meta">${inboxTip()}</p>
            ${recCardHtml}
            <button class="btn-secondary" id="retake-btn">Take the Quiz Again</button>
          </div>
          <div class="thanks-footer">
            <p class="subhead">We sent your fun personality results to:<br><strong>${escapeHtml(state.email)}</strong></p>
            <div class="heart-divider">
              <span class="heart-line"></span>
              <span class="heart">${heartSVG}</span>
              <span class="heart-line"></span>
            </div>
            <p class="thanks-footer-meta">${inboxTip()}</p>
          </div>
        </div>
      </div>`;
    $('#retake-btn').addEventListener('click', renderIntro);
  }

  function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[c]);
  }

  // ── Klaviyo submission ───────────────────────────────────────
  // 1. POST /client/subscriptions/ — creates/updates the profile, sets all
  //    trait properties, opts the subscriber in to email marketing, and adds
  //    them to the "Faffle Quiz Subscribers" list (so flows can send to them).
  // 2. POST /client/events/ — fires the "Quiz Completed" metric so the trait
  //    flow triggers and the right primary email lands in their inbox.
  function submitToKlaviyo() {
    // Underscore-keyed value used by the Klaviyo flow logic + email tokens,
    // plus a hyphenated `trait_handle` ready to drop straight into email URLs
    // pointing at the matching Shopify collection (`/collections/the-{handle}`).
    const primaryKey = TRAITS[state.primaryLetter].key;
    const properties = {
      primary_trait:   primaryKey,
      secondary_trait: state.secondaryLetter ? TRAITS[state.secondaryLetter].key : 'none',
      is_double_down:  state.isDoubleDown,
      quiz_date:       new Date().toISOString(),
      trait_handle:    'the-' + primaryKey.replace(/_/g, '-')
    };

    const subscriptionBody = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Faffle Quiz',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email:      state.email,
                first_name: state.firstName || null,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: KLAVIYO_LIST_ID }
          }
        }
      }
    };

    const eventBody = {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric:  { data: { type: 'metric',  attributes: { name: 'Quiz Completed' } } },
          profile: { data: { type: 'profile', attributes: { email: state.email } } }
        }
      }
    };

    const headers = {
      'Content-Type': 'application/json',
      'revision': KLAVIYO_REVISION
    };

    const subscribeURL = `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_PUBLIC_KEY}`;
    const eventURL     = `https://a.klaviyo.com/client/events/?company_id=${KLAVIYO_PUBLIC_KEY}`;

    return fetch(subscribeURL, {
      method: 'POST',
      headers,
      body: JSON.stringify(subscriptionBody)
    }).then(() => fetch(eventURL, {
      method: 'POST',
      headers,
      body: JSON.stringify(eventBody)
    })).then(() => {
      // Mark the quiz as completed in this browser so any auto-open triggers
      // (scroll depth, exit intent, etc.) skip showing the popup again.
      try { localStorage.setItem('faffle_quiz_completed', '1'); } catch (e) {}
    });
  }

  // ── Boot ─────────────────────────────────────────────────────
  // Expose a global opener so any page can pop the quiz on top of itself.
  // Triggers: class "js-open-quiz", link to #open-quiz, or link to /pages/quiz
  // (so existing menu/email/CTAs become popups instead of full-page nav).
  window.openFaffleQuiz = function () {
    var backdrop = document.getElementById('quiz-backdrop');
    if (!backdrop) return;
    backdrop.hidden = false;
    document.body.style.overflow = 'hidden';
    renderIntro();
  };

  window.closeFaffleQuiz = function () {
    var backdrop = document.getElementById('quiz-backdrop');
    if (!backdrop) return;
    backdrop.hidden = true;
    document.body.style.overflow = '';
  };

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.js-open-quiz, a[href="#open-quiz"], a[href$="/pages/quiz"], a[href*="/pages/quiz?"]');
    if (trigger) {
      e.preventDefault();
      window.openFaffleQuiz();
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    var closeBtn = document.getElementById('quiz-close');
    if (closeBtn) closeBtn.addEventListener('click', window.closeFaffleQuiz);

    // Ad landing: ?quiz=open (or #quiz) pops the quiz immediately on load.
    // Deliberate click on an ad = explicit intent, so this ignores the
    // session cap and completed flag the auto-show snippet respects.
    var wantsQuiz = false;
    try {
      wantsQuiz = new URLSearchParams(window.location.search).get('quiz') === 'open';
    } catch (e) { /* very old browser — fall through to hash check */ }
    if (window.location.hash === '#quiz') wantsQuiz = true;
    if (wantsQuiz) window.openFaffleQuiz();
  });
})();
